/**
 * DoubleCategoryExplainer — progressive-disclosure explainer for double
 * categories, the categorical structure behind the SEPARATE theorem and
 * the seam normal form.
 *
 * A double category has two kinds of arrows — horizontal (data flow) and
 * vertical (authority flow) — and composable squares (2-cells). CSK uses
 * the EMPTY square (the 2-cell that does not exist) as the SEPARATE seam.
 * The card includes:
 *
 *   - An interactive grid demo where users explore which cells are fillable
 *   - Connection to all four forbidden-square generators (G1–G4)
 *   - CalloutBox for PARTITION and ICG-Pa
 *   - Seam normal form connection (B1–B4 phases)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useState } from "react";
import { GRAY, THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  CalloutBox,
  DoubleCategoryDiagram,
  ExplainerCard,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import gridCells from "./double-category-grid-cells.data.json" with { type: "json" };

const ACCENT = THEOREM_COLOR.SEPARATE;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-double-category",
  kind: "Categorical structure",
  title: "Double category",
  subtitle:
    "Two-axis grid of horizontal (data flow) and vertical (authority flow) morphisms with squares as 2-cells",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "SEPARATE (S)", href: "#explainer-separate", accent: ACCENT },
  { label: "Beck-Chevalley", href: "#explainer-beck-chevalley" },
  { label: "Bisimulation", href: "#explainer-bisimulation" },
  { label: "Fibration / Opfibration", href: "#explainer-fibration" },
  { label: "Slice / Coslice", href: "#explainer-slice-category" },
];

interface CellState {
  readonly row: string;
  readonly col: string;
  readonly fillable: boolean;
  readonly generator: string;
}

const GRID_CELLS = gridCells as readonly CellState[];

const ROWS = ["Public", "Internal", "Secret"] as const;
const COLS = ["Public data", "Internal data", "Secret data"] as const;

function GridExplorerDemo(): React.JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);
  const enter = useCallback(
    (i: number): (() => void) =>
      () => {
        setHovered(i);
      },
    [],
  );
  const leave = useCallback(() => {
    setHovered(null);
  }, []);

  const hoveredCell = hovered !== null ? GRID_CELLS[hovered] : null;

  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.75rem",
        backgroundColor: withAlpha(ACCENT, "0D"),
        borderRadius: "0.5rem",
        border: `1px solid ${withAlpha(ACCENT, "33")}`,
      }}
      aria-label="Double category grid explorer"
    >
      <div
        style={{
          fontSize: "0.6875rem",
          fontWeight: 700,
          color: ACCENT,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: "0.5rem",
        }}
      >
        2-cell grid — hover to inspect (columns = data scope, rows = authority level)
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto repeat(3, 1fr)",
          gap: "2px",
          fontSize: "0.6875rem",
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        {/* header row */}
        <div />
        {COLS.map((c) => (
          <div
            key={c}
            style={{
              textAlign: "center",
              padding: "0.25rem",
              fontWeight: 600,
              color: GRAY[600],
              fontSize: "0.625rem",
            }}
          >
            {c}
          </div>
        ))}
        {/* grid body */}
        {ROWS.map((row, ri) => (
          <React.Fragment key={row}>
            <div
              style={{
                padding: "0.25rem 0.5rem 0.25rem 0",
                fontWeight: 600,
                color: GRAY[600],
                display: "flex",
                alignItems: "center",
                fontSize: "0.625rem",
              }}
            >
              {row}
            </div>
            {COLS.map((col, ci) => {
              const idx = ri * 3 + ci;
              const cell = GRID_CELLS[idx];
              if (!cell) return null;
              const isHovered = hovered === idx;
              return (
                <div
                  key={col}
                  onMouseEnter={enter(idx)}
                  onMouseLeave={leave}
                  style={{
                    width: "100%",
                    aspectRatio: "1.6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "0.25rem",
                    border: isHovered ? `2px solid ${ACCENT}` : `1px solid ${GRAY[300]}`,
                    backgroundColor: cell.fillable
                      ? withAlpha(ACCENT, "1A")
                      : withAlpha(GRAY[500], "0D"),
                    cursor: "pointer",
                    transition: "all 0.15s",
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: cell.fillable ? ACCENT : GRAY[500],
                  }}
                  role="gridcell"
                  aria-label={`${row} accessing ${col}: ${cell.fillable ? "allowed" : "seam (empty)"}`}
                >
                  {cell.fillable ? "α" : "✗"}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      {/* hover detail */}
      <div
        style={{
          marginTop: "0.5rem",
          minHeight: "2.5rem",
          padding: "0.375rem",
          borderRadius: "0.25rem",
          backgroundColor: "white",
          border: `1px solid ${GRAY[300]}`,
          fontSize: "0.75rem",
          color: GRAY[700],
        }}
      >
        {hoveredCell ? (
          <>
            <strong style={{ color: hoveredCell.fillable ? ACCENT : GRAY[600] }}>
              {hoveredCell.row} → {hoveredCell.col}:
            </strong>{" "}
            {hoveredCell.fillable ? (
              "2-cell exists — this authority level may access this data scope."
            ) : (
              <>
                <span style={{ color: GRAY[600] }}>
                  Empty square (seam). Generator: <strong>{hoveredCell.generator}</strong>{" "}
                  (Epistemic Lift) — knowing the data exists does not grant authority to access it.
                </span>
              </>
            )}
          </>
        ) : (
          <span style={{ color: GRAY[500], fontStyle: "italic" }}>
            Hover a cell to see whether the 2-cell is inhabited or empty.
          </span>
        )}
      </div>
    </div>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A <strong>double category</strong> has two kinds of arrows — horizontal (data flow) and
      vertical (authority flow) — and composable squares (2-cells) that fill when both directions
      exist. CSK uses the <em>empty</em> square — the 2-cell that does not exist — as the SEPARATE
      seam. The violation is not denied; it is structurally absent from the category.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of a spreadsheet grid: rows are authority levels, columns are data scopes. A filled
        cell means &ldquo;this authority level can access this data scope.&rdquo; An empty cell
        means &ldquo;crossing from this authority to that data is structurally absent.&rdquo;
      </p>
      <p>
        CSK&rsquo;s <strong>SEPARATE</strong> theorem guarantees that certain cells in this grid can
        never be filled. A horizontal arrow (data movement) and a vertical arrow (authority change)
        may each exist individually, but their combined square is unconstructable. The information
        barrier is not a runtime policy — it is an absent inhabitant of the 2-cell hom-set.
      </p>
      <p>
        <strong>Why this is stronger than access control:</strong> traditional systems deny access
        at runtime — there is a handler, a log entry, and a code path that could be bypassed. In a
        double category, the empty square has no code path at all. You cannot write a program that
        crosses the seam because the type of &ldquo;crossing programs&rdquo; is uninhabited.
      </p>
      <GridExplorerDemo />
    </>
  ),
  formal: (
    <>
      <p>
        A (strict) <strong>double category</strong> D has:
      </p>
      <ul>
        <li>
          A set of <strong>objects</strong> (nodes in the grid)
        </li>
        <li>
          <strong>Horizontal morphisms</strong> <MathInline>{tex`D_{h}`}</MathInline> (data flow)
          composing along the horizontal axis
        </li>
        <li>
          <strong>Vertical morphisms</strong> <MathInline>{tex`D_{v}`}</MathInline> (authority flow)
          composing along the vertical axis
        </li>
        <li>
          <strong>Squares</strong> (2-cells) filling commutative boundaries — given horizontal edges{" "}
          <MathInline>{tex`h, h'`}</MathInline> and vertical edges{" "}
          <MathInline>{tex`v, v'`}</MathInline>, a square <MathInline>{tex`\alpha`}</MathInline>{" "}
          inhabits <MathInline>{tex`\mathrm{Sq}(h, h';\, v, v')`}</MathInline>
        </li>
      </ul>
      <p>
        Composition happens independently in both directions (horizontal comp for squares in a row,
        vertical comp for squares in a column). An empty hom-set{" "}
        <MathInline>{tex`\mathrm{Sq}(f, g;\, h, k) = \varnothing`}</MathInline> declares that
        combination unconstructable — that is the SEPARATE seam.
      </p>
      <DoubleCategoryDiagram accent={ACCENT} />
      <p style={{ marginTop: "0.75rem" }}>
        The four <strong>forbidden square generators</strong> are:
      </p>
      <ul>
        <li>
          <strong>G1 (Ambient Effect):</strong> effect without authority — no free lunch
        </li>
        <li>
          <strong>G2 (Epistemic Lift):</strong> information → authority — knowing ≠ permission
        </li>
        <li>
          <strong>G3 (Time Reversal):</strong> retroactive justification — future cannot bless past
        </li>
        <li>
          <strong>G4 (Authority Duplication):</strong> copying inflates power — authority is scarce
        </li>
      </ul>
      <p>
        Every seam in CSK is generated by one or more of these four patterns. SEPARATE specifically
        targets G2: the knowledge of data existing in a scope does not create authority to access
        that scope.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>SEPARATE</strong> (S) models information-flow boundaries as empty squares: a
        horizontal morphism (data movement) and a vertical morphism (authority change) may both
        exist individually, but their combined 2-cell is unconstructable. This is strictly stronger
        than runtime access control — the violation is not denied, it is structurally absent from
        the category itself. The seam type is <em>diagonal</em> (IMMUNE mode) and the governance
        artifact is an <strong>authorized type conversion</strong>.
      </p>
      <p>
        The Seam Normal Form applies to every bridge that crosses a SEPARATE seam. Any legitimate
        crossing decomposes into four sequential phases:
      </p>
      <ol>
        <li>
          <strong>B1 (WITNESS)</strong> — present evidence of the crossing&rsquo;s justification
        </li>
        <li>
          <strong>B2 (AUTHORIZE)</strong> — governance validates the witness and grants a token
        </li>
        <li>
          <strong>B3 (EXECUTE)</strong> — the crossing occurs under the token&rsquo;s scope
        </li>
        <li>
          <strong>B4 (ACCOUNT)</strong> — accounting evidence is produced for audit
        </li>
      </ol>
      <p>
        The <strong>non-feedback invariant</strong> ensures B4 outputs never feed back to B2 inputs,
        neutralizing bootstrap attacks by construction.
      </p>
      <CalloutBox eyebrow="Safety Envelope — ICG-Pa" accent={ACCENT}>
        <p style={{ margin: 0 }}>
          The SDP <strong>PARTITION</strong> uses the parameterized self-dual adjunction to certify
          that boundary splitting and recombination are complementary. Deploying SEPARATE with
          PARTITION yields the <strong>ICG-Pa Safety Envelope</strong> — the strongest
          information-flow guarantee in CSK. The self-duality ensures that separating a scope and
          recombining it cannot introduce or destroy access rights; the joint envelope makes both
          unauthorized crossing and unauthorized merging unconstructable.
        </p>
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Seam theory",
      path: "theory/structural/SEAM-THEORY.md",
      note: "SEPARATE seam (diagonal, IMMUNE mode), four forbidden generators, SNF.",
    },
    {
      label: "Foundation/DoubleCategory/Seams.agda",
      path: "agda/Foundation/DoubleCategory/Seams.agda",
      note: "Mechanized DCSeam, DCBridge, SeamBridgePair — the double-category seam framework.",
    },
    {
      label: "Foundation/Seams/SeamNormalForm.agda",
      path: "agda/Foundation/Seams/SeamNormalForm.agda",
      note: "Mechanized SNF phases B1–B4 and the non-feedback invariant.",
    },
    {
      label: "Foundation/Syntax/Seams.agda",
      path: "agda/Foundation/Syntax/Seams.agda",
      note: "SEAM, BRIDGE, FORBIDDEN declaration syntax.",
    },
    {
      label: "Theorem-Canon.md",
      path: "docs/reference/Theorem-Canon.md",
      note: "Extended explanation of SEPARATE, PARTITION, and ICG-Pa Safety Envelope.",
    },
    {
      label: "SEAM-NORMAL-FORM.md",
      path: "theory/structural/SEAM-NORMAL-FORM.md",
      note: "Full specification of B1–B4 phases, non-feedback invariant, and composition safety.",
    },
  ],
};

export function DoubleCategoryExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<DoubleCategoryDiagram accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
